import { useMutation, useQuery, useQueryClient } from "react-query";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";

export const useIntegration = (key, config) => {
  return useQuery({
    ...config,
    queryKey: ["integrations", key],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations/${key}`,
        signal,
      }),
  });
};

export const useIntegrations = (config) => {
  return useQuery({
    ...config,
    queryKey: "integrations",
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations`,
        signal,
      }),
  });
};

export const useUpdateIntegrationMutation = (key, method = "POST", onToggle, skipValidation = false) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/${key}`,
        method,
        ...(data && { body: data }),
      }),
    onSuccess: () => {
      onToggle?.();
      if (!skipValidation) {
        client.removeQueries(["integrations"]);
        client.invalidateQueries(["integrations"]);
        client.invalidateQueries(["integrations", key]);
      }
    },
  });
};

export const useUpdateSearchIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/search`,
        method: "PUT",
        body: data,
      }),
    onSuccess: (response) => {
      client.setQueryData(["integrations", "search"], response.single);
    },
  });
};

export const useUpdateEmailIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/email`,
        method: "PUT",
        body: data,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "email"]);
    },
  });
};

export const useConfigureSearchIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/search/configure`,
        method: "PUT",
        body: data,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "search"]);
      client.invalidateQueries("system-status");
    },
  });
};

export const useTestIntegration = (type) => {
  if (!["search", "email", "pusher"].includes(type)) {
    throw new Error("Unsupported type, allowed: email,search,pusher");
  }
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/${type}/test-connection`,
        method: "POST",
        body: data,
      }),
    onSuccess: (response) => {
      if (!response.ok) {
        throw new Error(response.error ?? response.message);
      }
    },
  });
};

export const useDisconnectSearchIntegrationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/integrations/search/disconnect`,
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["integrations", "search"]);
      queryClient.invalidateQueries("system-status");
    },
  });
};

export const useConfigurePusherIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/pusher/configure`,
        method: "PUT",
        body: data,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "pusher"]);
      client.invalidateQueries("system-status");
    },
  });
};

export const useUpdatePusherIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/pusher`,
        method: "PUT",
        body: data,
      }),
    onSuccess: (response) => {
      client.setQueryData(["integrations", "pusher"], response.single);
    },
  });
};

export const useDisconnectPusherIntegrationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/integrations/pusher/disconnect`,
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["integrations", "pusher"]);
      queryClient.invalidateQueries("system-status");
    },
  });
};

export const useUpdateWebhookIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rest }) =>
      fetch({
        url: `${Angie.api_url}/integrations/webhooks/${id}`,
        method: "PUT",
        body: rest,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "webhooks"]);
    },
  });
};

export const useWebhookIntegrationsEventTypes = (config) => {
  return useQuery({
    ...config,
    queryKey: ["webhooks-event-types"],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/integrations/webhooks/event-types`,
        signal,
      }),
  });
};

export const useDeleteWebhookIntegrationMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) =>
      fetch({
        url: `${Angie.api_url}/integrations/webhooks/${id}`,
        method: "DELETE",
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "webhooks"]);
    },
  });
};

export const useAddWebhookIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/webhooks`,
        method: "POST",
        body: data,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "webhooks"]);
    },
  });
};

export const useUpdateSlackNotificationChannelMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rest }) =>
      fetch({
        url: `${Angie.api_url}/integrations/slack/notification-channels/${id}`,
        method: "PUT",
        body: rest,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "slack"]);
    },
  });
};

export const useConnectSlackIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/integrations/slack/connect`,
        method: "PUT",
        body: data,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "slack"]);
    },
  });
};

export const useDisconnectSlackIntegrationMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/integrations/slack`,
        method: "DELETE",
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "slack"]);
    },
  });
};

export const useDeleteSlackChannelMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) =>
      fetch({
        url: `${Angie.api_url}/integrations/slack/notification-channels/${id}`,
        method: "DELETE",
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "slack"]);
    },
  });
};

export const useUpdateSlackChannelMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...rest }) =>
      fetch({
        url: `${Angie.api_url}/integrations/slack/notification-channels/${id}`,
        method: "PUT",
        body: rest,
      }),
    onSuccess: () => {
      client.invalidateQueries(["integrations", "slack"]);
    },
  });
};
